import Modal from "../../../Component/Modal/Modal";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useContext, useState, useEffect } from "react"
import { Checkbox, FormControlLabel, TextField, IconButton, Chip, Radio, RadioGroup } from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import classes from "./Setting.module.css";
import TemplateSetting from "./TemplateSetting";
import PersonaSetting from "./PersonaSetting";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import { DiscoveryContext } from "../discovery-context";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import MUIMultiSelect from "../../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import { SITE_SUGGESTIONS } from "../../../utils/constant";
import { AuthContext } from "../../../context/auth-context";
import api from "../../../BrandOnBoarding/api";
import { resolveDefaultKeywordOptions, resolveMaxKeywords } from "../../../utils/keywordOptions";
import { hasBatchCapableLlm } from "../../../utils/batchCapableLlms";

const EAB_SITE_SUGGESTIONS = [
    { id: 10, name: "Collegevine", value: "collegevine.com" },
    { id: 11, name: "Niche", value: "niche.com" },
    { id: 12, name: "College Confidential", value: "collegeconfidential.com" },
];

export default function SettingsModal({
    open,
    onClose,
    category,
    categoryWiseMetric,
    metricSettingId,
    onCategoryTemplateChange,
    MetricSettingChangeHandle,
    categoryWiseCategoryType,
    onCategoryTypeChange,
    categoryWiseRankCompetitiveSet = {},
    onRankCompetitiveSetChange = () => {},
    rankMethodology = "by_category",
    onRankMethodologyChange = () => {},
    competitorsData = {},
    mainBrand = "",
    keyPlayers = {},
    keyPlayersLoading = false,
    personaSettings,
    setPersonaSettings,
    rbiType,
    setRbiType,
    keywords,
    setKeywords,
    conversationSettings,
    setConversationSettings,
    isBatchMode = false,
    onBatchModeChange = () => {},
    isNonBrandedQuestions = false,
    onNonBrandedQuestionsChange = () => {},
    isFactBasedQuestions = false,
    onFactBasedQuestionsChange = () => {},
    llm = [],
}) {
    const discoveryCtx = useContext(DiscoveryContext);
    const authCtx = useContext(AuthContext);
    const enableTestConvoContentMap = !!authCtx?.user?.enableTestBrandAuditConversationContentMapping;
    const [siteSuggestionOptions, setSiteSuggestionOptions] = useState(null);

    useEffect(() => {
        const customerId = authCtx?.user?.customer_id;
        if (!customerId) return;
        let cancelled = false;
        api.get(`/customers/${customerId}/site-suggestions`)
            .then((response) => {
                if (!cancelled) setSiteSuggestionOptions(response?.data?.siteSuggestions || []);
            })
            .catch(() => {
                if (!cancelled) setSiteSuggestionOptions(null); // fall back to hardcoded defaults below
            });
        return () => { cancelled = true; };
    }, [authCtx?.user?.customer_id]);

    const siteSuggestions = Array.isArray(siteSuggestionOptions)
        ? siteSuggestionOptions
        : authCtx?.user?.customer?.name === "EAB"
            ? [...SITE_SUGGESTIONS, ...EAB_SITE_SUGGESTIONS]
            : SITE_SUGGESTIONS;
    const isFinalTransactionCompleted =
        discoveryCtx?.state.finalTransactionStatus === "COMPLETED" ||
        discoveryCtx?.state.finalTransactionStatus === "RETRY";
    const isDisabled = discoveryCtx?.loading || isFinalTransactionCompleted
    const userMetricSetting = useSelector((store) => store.userSet);
    const templateOptions = userMetricSetting?.data?.map((ele) => ({
        name: ele.name,
        value: ele.id,
        isDefault: ele.isDefault,
    })) ?? [];

    const defaultTemplate = templateOptions.find(opt => opt.isDefault)?.name || "Standard Audit V2";

    const selectedTemplateData =
        (userMetricSetting?.data ?? []).find((ele) => ele.id == metricSettingId) ?? {};

    const handleNonBrandedQuestionsChange = (checked) => {
        if (checked && !(selectedTemplateData?.["non-branded-questions"]?.length > 0)) {
            toast.error("The selected template has no Open Ended Questions configured. Add some in Template Settings first.");
            return;
        }
        onNonBrandedQuestionsChange(checked);
    };

    const handleFactBasedQuestionsChange = (checked) => {
        if (checked && !(selectedTemplateData?.["fact-based-questions"]?.length > 0)) {
            toast.error("The selected template has no Fact Based Questions configured. Add some in Template Settings first.");
            return;
        }
        onFactBasedQuestionsChange(checked);
    };

    const overrideCount = category.filter(cat =>
        categoryWiseMetric[cat] && Number(categoryWiseMetric[cat]) !== Number(metricSettingId)
    ).length;

    const defaultKeywordOptions = resolveDefaultKeywordOptions(authCtx?.customerUiInfo);
    const maxKeywords = resolveMaxKeywords(authCtx?.user?.email, defaultKeywordOptions.length);
    const [customKeywordInput, setCustomKeywordInput] = useState("");

    const allKeywordOptions = [
        ...defaultKeywordOptions,
        ...keywords.filter(k => !defaultKeywordOptions.includes(k))
    ];

    const handleKeywordToggle = (keyword, checked) => {
        if (checked) {
            if (keywords.length >= maxKeywords) return;
            setKeywords(prev => [...prev, keyword]);
        } else {
            setKeywords(prev => prev.filter(k => k !== keyword));
        }
    };

    const handleAddCustomKeyword = () => {
        const trimmed = customKeywordInput.trim();
        if (!trimmed) return;
        if (allKeywordOptions.some(k => k.toLowerCase() === trimmed.toLowerCase())) return;
        if (keywords.length >= maxKeywords) {
            setKeywords(prev => [...prev.slice(0, maxKeywords - 1), trimmed]);
        } else {
            setKeywords(prev => [...prev, trimmed]);
        }
        setCustomKeywordInput("");
    };

    const handleRemoveCustomKeyword = (keyword) => {
        setKeywords(prev => prev.filter(k => k !== keyword));
    };

    const handleApply = () => {
        onClose();
    };

    const handleSiteChange = (selected) => {
        setConversationSettings(prev => ({ ...prev, selectedSites: selected }));
    };
    return (
        <Modal
            handleClose={onClose}
            open={open}
            title="Configuration Settings"
            width={authCtx?.user?.is_custom_rank ? "950" : "750"}
            showActions={false}
        >
            <div className={classes.modalContent}>
                <div className={classes.modalBody}>
                    {!!authCtx?.user?.is_batch_mode_enabled && hasBatchCapableLlm(llm) && (
                        <div className={classes.settingSection}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isBatchMode}
                                        onChange={(e) => onBatchModeChange(e.target.checked)}
                                        disabled={isDisabled}
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": { color: "#2dd4bf" },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label="Batch Mode"
                                sx={{
                                    ".MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </div>
                    )}

                    {!!authCtx?.customerUiInfo?.is_non_branded_questions && (
                        <div className={classes.settingSection} style={{ display: "flex", flexDirection: "row", gap: "1.5rem" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isNonBrandedQuestions}
                                        onChange={(e) => handleNonBrandedQuestionsChange(e.target.checked)}
                                        disabled={isDisabled}
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": { color: "#2dd4bf" },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label="Open Ended Questions"
                                sx={{
                                    ".MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isFactBasedQuestions}
                                        onChange={(e) => handleFactBasedQuestionsChange(e.target.checked)}
                                        disabled={isDisabled}
                                        sx={{
                                            color: "#d1d5db",
                                            "&.Mui-checked": { color: "#2dd4bf" },
                                            padding: "4px",
                                        }}
                                    />
                                }
                                label="Fact Based Questions"
                                sx={{
                                    ".MuiFormControlLabel-label": {
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </div>
                    )}

                    {/* Template Settings Section */}
                    <div className={classes.settingSection}>
                        <TemplateSetting
                            templateOptions={templateOptions}
                            metricSettingId={metricSettingId === "default" ? "default" : Number(metricSettingId)}
                            MetricSettingChangeHandle={MetricSettingChangeHandle}
                            category={category}
                            categoryWiseMetric={categoryWiseMetric}
                            onCategoryTemplateChange={onCategoryTemplateChange}
                            defaultTemplate={defaultTemplate}
                            overrideCount={overrideCount}
                            showAddButton={!!authCtx?.user?.is_template_setting_enabled}
                            showCategoryType
                            categoryWiseCategoryType={categoryWiseCategoryType}
                            onCategoryTypeChange={onCategoryTypeChange}
                            showRankCompetitiveSet={!!authCtx?.user?.is_custom_rank}
                            categoryWiseRankCompetitiveSet={categoryWiseRankCompetitiveSet}
                            onRankCompetitiveSetChange={onRankCompetitiveSetChange}
                            competitorsData={competitorsData}
                            mainBrand={mainBrand}
                            keyPlayers={keyPlayers}
                            keyPlayersLoading={keyPlayersLoading}
                        />
                    </div>

                    {/* Rank Methodology Section — only when the user has this feature enabled */}
                    {!!authCtx?.user?.is_custom_rank && (
                        <div className={classes.settingSection}>
                            <h4 className={classes.settingSectionTitle}>Rank Methodology</h4>
                            <RadioGroup
                                value={rankMethodology}
                                onChange={(e) => onRankMethodologyChange(e.target.value)}
                            >
                                <FormControlLabel
                                    value="by_category"
                                    control={<Radio disabled={isDisabled} sx={{ "&.Mui-checked": { color: "#2dd4bf" } }} />}
                                    label="By Category"
                                    sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                                />
                                <FormControlLabel
                                    value="by_named_competitor"
                                    control={<Radio disabled={isDisabled} sx={{ "&.Mui-checked": { color: "#2dd4bf" } }} />}
                                    label="By Named Competitor"
                                    sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                                />
                            </RadioGroup>
                        </div>
                    )}

                    {/* Persona Settings Section */}
                    <div className={classes.settingSection}>
                        <PersonaSetting
                            personaSettings={personaSettings}
                            setPersonaSettings={setPersonaSettings}
                        />
                    </div>

                    {/* Conversation Settings Section */}
                    <div className={classes.settingSection}>
                        <h4 className={classes.settingSectionTitle}>Conversation</h4>
                         <p className={classes.sectionSubtitle}>
                               {enableTestConvoContentMap
                                   ? `These settings apply during both Test ${ENTITY_LABELS.audit} and Final ${ENTITY_LABELS.audit} runs. Configure site suggestions and content filters that will be used when generating conversation results.`
                                   : `These settings apply only during the Final ${ENTITY_LABELS.audit} run. Configure site suggestions and content filters that will be used when generating conversation results.`}
                         </p>
                        <div className={classes.conversationFields}>
                            <MUIMultiSelect
                                disabled={isDisabled || (!discoveryCtx?.state?.launchRecord?.length && !enableTestConvoContentMap)}
                                label={"Site Suggestions"}
                                options={siteSuggestions}
                                value={conversationSettings.selectedSites}
                                customWidth={classes.fullWidth}
                                onChange={handleSiteChange}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={conversationSettings.excludeBrandOwnedSources}
                                        onChange={(e) =>
                                            setConversationSettings(prev => ({
                                                ...prev,
                                                excludeBrandOwnedSources: e.target.checked,
                                            }))
                                        }
                                        disabled={isDisabled || (!discoveryCtx?.state?.launchRecord?.length && !enableTestConvoContentMap)}
                                    />
                                }
                                label="Eliminate brand content/posts"
                            />
                        </div>
                    </div>

                    {/* RBI Type Settings Section */}
                    {/* <div className={classes.settingSection}>
                         <h4 className={classes.settingSectionTitle}>RBI Type</h4>
                        <MUISelect
                            fullWidth={true}
                            optionValue={rbiType}
                            // label={"RBI Type"}
                            disabled={isDisabled}
                            options={[
                                { name: "Propensity To Recommend", value: "propensityToRecommend" },
                                { name: "Purchase Intent", value: "propensityToBuy" },
                            ]}
                            onChange={(e) => setRbiType(e.target.value)}
                        />
                    </div>

                    {/* Keywords Settings Section */}
                    <div className={classes.settingSection}>
                        <h4 className={classes.settingSectionTitle}>
                            {ENTITY_LABELS.rank} Attributes
                            <span className={classes.keywordCount}>({keywords.length}/{maxKeywords} selected)</span>
                        </h4>
                        <div className={classes.keywordsContainer}>
                            {defaultKeywordOptions.map((keyword) => (
                                <FormControlLabel
                                    key={keyword}
                                    control={
                                        <Checkbox
                                            checked={keywords.includes(keyword)}
                                            onChange={(e) => handleKeywordToggle(keyword, e.target.checked)}
                                            disabled={isDisabled || (!keywords.includes(keyword) && keywords.length >= maxKeywords)}
                                            sx={{
                                                color: '#d1d5db',
                                                '&.Mui-checked': { color: '#2dd4bf' },
                                                padding: '4px'
                                            }}
                                        />
                                    }
                                    label={keyword}
                                    sx={{
                                        "&.MuiFormControlLabel-root": {
                                            padding: "0.5rem 1rem",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "0.75rem",
                                            margin: "0 !important",
                                            background: keywords.includes(keyword) ? "#f0fdfa" : "white",
                                        },
                                        ".MuiFormControlLabel-label": {
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                        }
                                    }}
                                />
                            ))}
                        </div>

                        {/* Custom keywords */}
                        {keywords.filter(k => !defaultKeywordOptions.includes(k)).length > 0 && (
                            <div className={classes.customKeywordsChips}>
                                {keywords.filter(k => !defaultKeywordOptions.includes(k)).map((keyword) => (
                                    <Chip
                                        key={keyword}
                                        label={keyword}
                                        onDelete={isDisabled ? undefined : () => handleRemoveCustomKeyword(keyword)}
                                        deleteIcon={<CloseIcon style={{ fontSize: '16px' }} />}
                                        sx={{
                                            background: '#f0fdfa',
                                            border: '1px solid #2dd4bf',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Add custom keyword input */}
                        <div className={classes.customKeywordInput}>
                            <TextField
                                size="small"
                                placeholder="Add custom Attribute..."
                                value={customKeywordInput}
                                onChange={(e) => setCustomKeywordInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddCustomKeyword();
                                    }
                                }}
                                disabled={isDisabled}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={handleAddCustomKeyword}
                                disabled={isDisabled || !customKeywordInput.trim()}
                                sx={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.5rem',
                                    color: '#2dd4bf',
                                    '&:hover': { background: '#f0fdfa' }
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}